import React, { useState, useEffect, useRef } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  Description as DescriptionIcon,
  Search as SearchIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { renderAsync } from 'docx-preview';

// Constantes para tipos MIME de Office
const OFFICE_WORD_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword'
]);

const OFFICE_EXCEL_MIMES = new Set([
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel'
]);

const HistorialAvanceTgu = ({ tramiteId, refreshTrigger }) => {
  const [avances, setAvances] = useState([]);
  const [adjuntos, setAdjuntos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAdjuntos, setShowAdjuntos] = useState(true);
  const [descargandoId, setDescargandoId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [objectUrl, setObjectUrl] = useState(null);
  const [previewType, setPreviewType] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const docxContainerRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        console.log(`Cargando datos para trámite ID: ${tramiteId}`);
        
        // Fetch avances
        const avancesResponse = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/avance/listar/${tramiteId}/`);
        
        if (!avancesResponse.ok) {
          throw new Error(`Error ${avancesResponse.status} al cargar avances`);
        }
        
        const avancesData = await avancesResponse.json();
        console.log('Avances cargados:', avancesData);
        setAvances(Array.isArray(avancesData) ? avancesData : []);
        
        // Fetch adjuntos del trámite
        const adjuntosUrl = `https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/adjuntos/listar/?entity_id=${tramiteId}`;
        console.log('URL de adjuntos:', adjuntosUrl);
        
        const adjuntosResponse = await fetch(adjuntosUrl);
        
        if (!adjuntosResponse.ok) {
          throw new Error(`Error ${adjuntosResponse.status} al cargar adjuntos`);
        }
        
        const adjuntosData = await adjuntosResponse.json();
        console.log('Adjuntos cargados:', adjuntosData);
        setAdjuntos(adjuntosData.adjuntos || []);
        
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (tramiteId) fetchData();
  }, [tramiteId, refreshTrigger]);

  const normalizarFecha = (fechaStr) => {
    if (!fechaStr) return null;
    
    if (fechaStr.includes(' ') && !fechaStr.includes('T')) {
      // Agregar 'T' y zona horaria de Honduras (UTC-6)
      return new Date(fechaStr.replace(' ', 'T') + '-06:00');
    }
    
    // Formato ISO: "2025-08-25T08:40:40.087000" 
    if (fechaStr.includes('T')) {
      // Si no tiene información de zona horaria, asumir UTC-6 (Honduras)
      if (!fechaStr.includes('Z') && !fechaStr.includes('+') && !fechaStr.includes('-')) {
        return new Date(fechaStr + '-06:00');
      }
      return new Date(fechaStr);
    }
    
    // Intentar parsear directamente como último recurso
    return new Date(fechaStr);
  };

  // Función para formatear fecha en zona horaria de Honduras
  const formatearFechaHonduras = (fechaStr) => {
    const fecha = normalizarFecha(fechaStr);
    if (!fecha) return null;
    
    return fecha.toLocaleString('es-HN', {
      timeZone: 'America/Tegucigalpa',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Función para obtener el contenido base64 de un adjunto
  const fetchAdjunto = async (adjuntoId) => {
    const response = await fetch(`https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/adjuntos/base64/?id=${adjuntoId}`);
    if (!response.ok) {
      throw new Error(`Error ${response.status} al obtener adjunto`);
    }
    return await response.json();
  };

  // Función para convertir base64 to Blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };

  // Función para convertir base64 a ArrayBuffer
  const base64ToArrayBuffer = (base64) => {
    const binaryString = atob(base64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  };

  // Función para determinar el nombre del archivo
  const resolveFileName = (adjunto, mimeType) => {
    if (adjunto.nombre) return adjunto.nombre;
    
    const extension = mimeType.startsWith('image/') ? '.jpg' : 
                     mimeType === 'application/pdf' ? '.pdf' :
                     OFFICE_WORD_MIMES.has(mimeType) ? '.docx' :
                     OFFICE_EXCEL_MIMES.has(mimeType) ? '.xlsx' : '.bin';
                     
    return `archivo_${adjunto.id}${extension}`;
  };

  // Función para renderizar documentos DOCX
  const renderDocx = async (arrayBuffer, container) => {
    try {
      await renderAsync(arrayBuffer, container, null, {
        className: "docx", 
        inWrapper: true,
        ignoreWidth: false, 
        ignoreHeight: false, 
        ignoreFonts: false, 
        breakPages: true, 
        ignoreLastRenderedPageBreak: true, 
        experimental: false, 
        trimXmlDeclaration: true, 
        useBase64URL: false, 
        useMathMLPolyfill: false, 
        showChanges: false,
      });
    } catch (error) {
      console.error('Error al renderizar DOCX:', error);
      container.innerHTML = '<p>No se pudo previsualizar el documento.</p>';
    }
  };

  // Función para limpiar la previsualización anterior
  const cleanupPreview = () => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
      setObjectUrl(null);
    }
    setPreviewHtml('');
    if (docxContainerRef.current) {
      docxContainerRef.current.innerHTML = '';
    }
  };

  // Función precisa para relacionar adjuntos con avances (CON ZONA HORARIA)
  const getAdjuntosRelacionados = (avance, margenSegundos = 10) => {
    const MARGEN_MS = margenSegundos * 1000;

    return adjuntos.filter(adj => {
      try {
        // Verificar usuario (deben coincidir - ambos convertidos a string)
        if (adj.usuario_id?.toString() !== avance.usuarioid?.toString()) {
          return false;
        }

        // Convertir ambas fechas con zona horaria correcta
        const fechaAdjuntoUTC = normalizarFecha(adj.fecha);
        const fechaAvanceUTC = normalizarFecha(avance.fechaavance);
        
        if (!fechaAdjuntoUTC || !fechaAvanceUTC) {
          return false;
        }
        
        // Calcular diferencia en milisegundos
        const diferenciaTiempo = Math.abs(fechaAdjuntoUTC.getTime() - fechaAvanceUTC.getTime());
        
        // Debug: mostrar información de comparación
        console.log(`Comparando avance ${avance.id} con adjunto ${adj.id}:`);
        console.log(`- Fecha avance: ${avance.fechaavance} → ${fechaAvanceUTC}`);
        console.log(`- Fecha adjunto: ${adj.fecha} → ${fechaAdjuntoUTC}`);
        console.log(`- Diferencia: ${diferenciaTiempo}ms (${diferenciaTiempo <= MARGEN_MS ? 'COINCIDE' : 'NO COINCIDE'})`);
        
        return diferenciaTiempo <= MARGEN_MS;
        
      } catch (error) {
        console.error('Error al relacionar adjuntos:', error);
        return false;
      }
    });
  };

  // Función para manejar la visualización de archivos
  const handleViewFile = async (adjunto) => {
    try {
      cleanupPreview(); // limpiar previsualización anterior
      const { base64, tipo_archivo } = await fetchAdjunto(adjunto.id);
      const mime = (tipo_archivo || '').toLowerCase();

      // Imagen
      if (mime.startsWith('image/')) {
        const blob = base64ToBlob(base64, mime);
        const url = URL.createObjectURL(blob);
        setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
        setObjectUrl(url);
        setPreviewType('image');
        setModalOpen(true);
        return;
      }

      // PDF
      if (mime === 'application/pdf') {
        const blob = base64ToBlob(base64, mime);
        const url = URL.createObjectURL(blob);
        setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
        setObjectUrl(url);
        setPreviewType('pdf');
        setModalOpen(true);
        return;
      }

      // DOCX
      if (OFFICE_WORD_MIMES.has(mime)) {
        const ab = base64ToArrayBuffer(base64);
        setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
        setPreviewType('docx');
        setModalOpen(true);
        // Renderizar dentro del contenedor del modal
        await renderDocx(ab, docxContainerRef.current);
        return;
      }

      // XLSX
      if (OFFICE_EXCEL_MIMES.has(mime)) {
        const ab = base64ToArrayBuffer(base64);
        const workbook = XLSX.read(ab, { type: 'array' });
        // Tomar la primera hoja por simplicidad
        const firstSheetName = workbook.SheetNames[0];
        const ws = workbook.Sheets[firstSheetName];
        // Generar HTML de la hoja
        const html = XLSX.utils.sheet_to_html(ws, { header: '', footer: '' });
        setSelectedFile({ ...adjunto, mime, fileName: resolveFileName(adjunto, mime) });
        setPreviewType('xlsx');
        setPreviewHtml(html);
        setModalOpen(true);
        return;
      }

      // Otros tipos: descargar directamente
      await handleDescargarAdjunto(adjunto.id, resolveFileName(adjunto, mime));
    } catch (err) {
      console.error('Error al obtener archivo base64:', err);
      alert('Error al previsualizar el archivo');
    }
  };

  // Función para descargar archivos
  const handleDescargarAdjunto = async (adjuntoId, nombreArchivo) => {
    setDescargandoId(adjuntoId);
    try {
      const { base64, tipo_archivo } = await fetchAdjunto(adjuntoId);
      
      if (base64 && tipo_archivo) {
        const blob = base64ToBlob(base64, tipo_archivo);
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = nombreArchivo || `archivo_${adjuntoId}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error al descargar archivo:', error);
      alert('Error al descargar el archivo');
    } finally {
      setDescargandoId(null);
    }
  };

  // Función para cerrar el modal
  const handleCloseModal = () => {
    cleanupPreview();
    setModalOpen(false);
    setSelectedFile(null);
    setPreviewType(null);
  };

  // Filtrar avances por término de búsqueda
  const filteredAvances = avances.filter(avance =>
    avance.descripcionavance?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    avance.usuarioid?.toString().includes(searchTerm)
  );

  if (error) {
    return (
      <Paper elevation={3} style={{ padding: '2rem', backgroundColor: '#F8F8FF' }}>
        <Alert severity="error" onClose={() => setError(null)}>
          Error al cargar el historial: {error}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper elevation={3} style={{ padding: '2rem', maxHeight: '80vh', backgroundColor: '#F5F5F5', display: 'flex', flexDirection: 'column' }}>
        {/* Encabezado */}
        <Box 
          sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2,
            cursor: 'pointer',
            backgroundColor: '#f5f5f5',
            p: 2,
            borderRadius: 1
          }}
          onClick={() => setShowAdjuntos(!showAdjuntos)}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            Historial de Avances ({avances.length})
            {loading && <CircularProgress size={20} sx={{ ml: 2 }} />}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
              {showAdjuntos ? 'Ocultar adjuntos' : 'Mostrar adjuntos'}
            </Typography>
            <IconButton size="small">
              {showAdjuntos ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        </Box>

        {/* Barra de búsqueda */}
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Buscar en avances..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        
        {/* Contenedor con scroll */}
        <Box sx={{ overflowY: 'auto', flexGrow: 1 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" sx={{ py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2 }}>Cargando avances...</Typography>
            </Box>
          ) : filteredAvances.length === 0 ? (
            <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 3 }}>
              {searchTerm ? 'No se encontraron avances que coincidan con la búsqueda' : 'No hay avances registrados para este trámite.'}
            </Typography>
          ) : (
            <List>
              {filteredAvances.map((avance) => {
                const adjuntosRelacionados = getAdjuntosRelacionados(avance);
                
                console.log(`Avance ${avance.id} tiene ${adjuntosRelacionados.length} adjuntos relacionados`);
                adjuntosRelacionados.forEach(adj => {
                  console.log(`  - Adjunto ${adj.id}: ${adj.fecha} - ${adj.nombre}`);
                });
                
                return (
                  <React.Fragment key={avance.id}>
                    <ListItem alignItems="flex-start" sx={{ px: 0 }}>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <DescriptionIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {formatearFechaHonduras(avance.fechaavance) || 'Fecha no disponible'}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <>
                            <Typography variant="body2" color="text.primary" paragraph sx={{ mt: 1 }}>
                              {avance.descripcionavance}
                            </Typography>
                            {showAdjuntos && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                  Adjuntos ({adjuntosRelacionados.length})
                                </Typography>
                                {adjuntosRelacionados.length > 0 ? (
                                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                    {adjuntosRelacionados.map(adjunto => (
                                      <Chip
                                        key={adjunto.id}
                                        icon={<DescriptionIcon />}
                                        label={adjunto.nombre || `Archivo_${adjunto.id}`}
                                        onClick={() => handleViewFile(adjunto)}
                                        onDelete={() => handleDescargarAdjunto(adjunto.id, adjunto.nombre)}
                                        deleteIcon={
                                          descargandoId === adjunto.id ? 
                                            <CircularProgress size={20} /> : 
                                            <DownloadIcon />
                                        }
                                        variant="outlined"
                                        sx={{ cursor: 'pointer' }}
                                        disabled={descargandoId === adjunto.id}
                                      />
                                    ))}
                                  </Box>
                                ) : (
                                  <Typography variant="caption" color="textSecondary">
                                    No hay archivos adjuntos para este avance.
                                  </Typography>
                                )}
                              </Box>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                    <Divider component="li" sx={{ my: 1 }} />
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>
      </Paper>

      {/* Modal para previsualización de archivos */}
      <Dialog 
        open={modalOpen} 
        onClose={handleCloseModal}
        maxWidth="lg"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            height: '90vh'
          }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedFile?.fileName || 'Vista previa'}
            </Typography>
            <IconButton onClick={handleCloseModal}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {previewType === 'image' && objectUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <img 
                src={objectUrl} 
                alt={selectedFile?.fileName} 
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          )}
          
          {previewType === 'pdf' && objectUrl && (
            <Box sx={{ height: '100%' }}>
              <iframe 
                src={objectUrl} 
                width="100%" 
                height="100%" 
                title={selectedFile?.fileName}
                style={{ border: 'none' }}
              />
            </Box>
          )}
          
          {previewType === 'docx' && (
            <Box 
              ref={docxContainerRef}
              sx={{ 
                overflow: 'auto',
                padding: '20px',
                '& .docx': {
                  padding: '0',
                  margin: '0'
                }
              }}
            />
          )}
          
          {previewType === 'xlsx' && (
            <Box 
              dangerouslySetInnerHTML={{ __html: previewHtml }}
              sx={{ overflow: 'auto', padding: '20px' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => handleDescargarAdjunto(selectedFile?.id, selectedFile?.fileName)}
            startIcon={<DownloadIcon />}
          >
            Descargar
          </Button>
          <Button onClick={handleCloseModal}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default HistorialAvanceTgu;